"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * The legal content — Datenschutzerklärung/Privacy, AGB/Terms and
 * Impressum/Imprint (T10, expanded 2026-08 for owner item 24), aimed at
 * EU/German professional standards (GDPR Art. 12-14 incl. Art. 9
 * health-data consent, §5 TMG/DDG, §312j BGB, Widerrufsrecht). ADR-013's
 * draft notice was removed 2026-08-27 after the attorney sign-off recorded
 * in docs/legal-handoff.md. The Impressum still has owner-supplied `[...]`
 * placeholders for operator identity (not invented here) — those stay
 * until the owner fills them.
 *
 * Rendered only on the standalone pages /datenschutz, /bedingungen and
 * /impressum (routes/LegalPage.tsx) — each document has its own real URL,
 * no modal.
 *
 * ADR-085 IAP-4: with the BUILD-TIME `VITE_BILLING_MODE=app_store` (lib/billingMode.ts; default `whop` = today's text, untouched) the
 * date line, the privacy passages on data, legal basis, recipients, retention and deletion tools, and the terms passages on pricing,
 * the pilot note, cancelling and withdrawal switch to their `*AppStore` keys, and the terms gain four clauses (billing through Apple,
 * changing your plan, refunds and access, memberships bought on bitebalance.de through Whop). Those texts are the ENGINEERING DRAFT of
 * docs/legal-draft-apple-billing-2026-10-04.md (founder update 2026-10-05: Apple is the seller of iOS subscriptions; the web keeps
 * only the Whop memberships bought before the cutover) — an attorney confirms them before the founder deploys with that mode.
 */

import type { ReactNode } from "react"
import { useI18n } from "@/i18n"
import { BILLING_MODE } from "@/lib/billingMode"
import type { LegalPanel } from "@/lib/legalRoutes"

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h4 className="text-sm font-semibold text-ink">{heading}</h4>
      <div className="text-sm whitespace-pre-line text-ink-2">{children}</div>
    </div>
  )
}

export function LegalBody({ panel }: { panel: LegalPanel }) {
  const { t } = useI18n()
  const appStore = BILLING_MODE === "app_store"
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        {t(appStore ? "landingPage.legal.lastUpdatedAppStore" : "landingPage.legal.lastUpdated")}
      </p>

      {panel === "privacy" && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-2">{t("landingPage.legal.privacyIntro")}</p>
          <Section heading={t("landingPage.legal.privacyControllerHeading")}>
            {t("landingPage.legal.privacyControllerBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyDataHeading")}>
            {appStore
              ? t("landingPage.legal.privacyDataBodyAppStore")
              : t("landingPage.legal.privacyDataBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyLegalBasisHeading")}>
            {appStore
              ? t("landingPage.legal.privacyLegalBasisBodyAppStore")
              : t("landingPage.legal.privacyLegalBasisBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyRecipientsHeading")}>
            {appStore
              ? t("landingPage.legal.privacyRecipientsBodyAppStore")
              : t("landingPage.legal.privacyRecipientsBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyStorageHeading")}>
            {t("landingPage.legal.privacyStorageBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyMetaPixelHeading")}>
            {t("landingPage.legal.privacyMetaPixelBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyMetaSdkHeading")}>
            {t("landingPage.legal.privacyMetaSdkBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyRetentionHeading")}>
            {appStore
              ? t("landingPage.legal.privacyRetentionBodyAppStore")
              : t("landingPage.legal.privacyRetentionBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyContactHeading")}>
            {t("landingPage.legal.privacyContactBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyRightsHeading")}>
            {t("landingPage.legal.privacyRightsBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyToolsHeading")}>
            {appStore
              ? t("landingPage.legal.privacyToolsBodyAppStore")
              : t("landingPage.legal.privacyToolsBody")}
          </Section>
          <Section heading={t("landingPage.legal.privacyAiHeading")}>
            {t("landingPage.legal.privacyAiBody")}
          </Section>
          <p className="text-xs text-ink-2">{t("landingPage.legal.privacyMinorsBody")}</p>
          <p className="text-sm text-ink-2">
            {t("landingPage.legal.questionsEmail")}{" "}
            <a className="underline" href={`mailto:${t("landingPage.legal.privacyEmail")}`}>
              {t("landingPage.legal.privacyEmail")}
            </a>
          </p>
        </div>
      )}

      {panel === "terms" && (
        <div className="flex flex-col gap-4">
          <Section heading={t("landingPage.legal.termsProviderHeading")}>
            {t("landingPage.legal.termsProviderBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsSubjectHeading")}>
            {t("landingPage.legal.termsSubjectBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsMedicalHeading")}>
            {t("landingPage.legal.termsMedicalBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsPricingHeading")}>
            {appStore
              ? t("landingPage.legal.termsPricingBodyAppStore")
              : t("landingPage.legal.termsPricingBody")}
          </Section>
          {appStore && (
            <Section heading={t("landingPage.legal.termsAppleBillingHeading")}>
              {t("landingPage.legal.termsAppleBillingBody")}
            </Section>
          )}
          <p className="rounded-bb-md bg-cream px-3 py-2 text-sm font-medium text-ink">
            {appStore
              ? t("landingPage.legal.termsPilotNoteAppStore")
              : t("landingPage.legal.termsPilotNote")}
          </p>
          <Section heading={t("landingPage.legal.termsCancelHeading")}>
            {appStore
              ? t("landingPage.legal.termsCancelBodyAppStore")
              : t("landingPage.legal.termsCancelBody")}
          </Section>
          {appStore && (
            <Section heading={t("landingPage.legal.termsPlanChangeHeading")}>
              {t("landingPage.legal.termsPlanChangeBody")}
            </Section>
          )}
          <Section heading={t("landingPage.legal.termsWithdrawalHeading")}>
            {appStore
              ? t("landingPage.legal.termsWithdrawalBodyAppStore")
              : t("landingPage.legal.termsWithdrawalBody")}
          </Section>
          {appStore && (
            <Section heading={t("landingPage.legal.termsRefundsHeading")}>
              {t("landingPage.legal.termsRefundsBody")}
            </Section>
          )}
          {appStore && (
            <Section heading={t("landingPage.legal.termsWebMembershipsHeading")}>
              {t("landingPage.legal.termsWebMembershipsBody")}
            </Section>
          )}
          <Section heading={t("landingPage.legal.termsLiabilityHeading")}>
            {t("landingPage.legal.termsLiabilityBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsAiHeading")}>
            {t("landingPage.legal.termsAiBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsChangesHeading")}>
            {t("landingPage.legal.termsChangesBody")}
          </Section>
          <Section heading={t("landingPage.legal.termsLawHeading")}>
            {t("landingPage.legal.termsLawBody")}
          </Section>
          <p className="text-sm text-ink-2">
            {t("landingPage.legal.questionsEmail")}{" "}
            <a className="underline" href={`mailto:${t("landingPage.legal.contactEmail")}`}>
              {t("landingPage.legal.contactEmail")}
            </a>
          </p>
        </div>
      )}

      {panel === "imprint" && (
        <div className="flex flex-col gap-4">
          <Section heading={t("landingPage.legal.imprintOperatorHeading")}>
            {t("landingPage.legal.imprintOperatorBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintContactHeading")}>
            {t("landingPage.legal.imprintContactBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintVatHeading")}>
            {t("landingPage.legal.imprintVatBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintResponsibleHeading")}>
            {t("landingPage.legal.imprintResponsibleBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintAppStoreHeading")}>
            {t("landingPage.legal.imprintAppStoreBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintDisputeHeading")}>
            {t("landingPage.legal.imprintDisputeBody")}
          </Section>
          <Section heading={t("landingPage.legal.imprintTrademarkHeading")}>
            {t("landingPage.legal.imprintTrademarkBody")}
          </Section>
          <p className="text-sm text-ink-2">
            {t("landingPage.legal.questionsEmail")}{" "}
            <a className="underline" href={`mailto:${t("landingPage.legal.contactEmail")}`}>
              {t("landingPage.legal.contactEmail")}
            </a>
          </p>
        </div>
      )}
    </div>
  )
}
